import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useFela } from 'react-fela';
import { Text } from '../common';
import Curve from './Curve';
import { CHROMECAST_STATES } from '../utils';
import storage from '../../../player/utils/storage';

const panelHeadingRules = ({ theme }) => ({
  color: theme.color.light,
  fontSize: theme.fontSize.loud,
  fontWeight: '600',
  marginBottom: theme.margin.small,
});

const labelContainerRules = () => ({
  bottom: '145px',
  position: 'absolute',
  right: '140px',
  zIndex: '2',
});

const border = 10000;
const holeRadius = 45;
const holeRightOffset = 35;

const bgRules = ({ theme }) => ({
  background: 'transparent',
  border: `${border}px solid ${theme.color.overlayDarkHeavy}`,
  borderRadius: '50%',
  bottom: `${0 - border}px`,
  boxSizing: 'content-box',
  height: `${holeRadius}px`,
  position: 'absolute',
  right: `${holeRightOffset - border}px`,
  width: `${holeRadius}px`,
  zIndex: '1',
});

const wrapperRules = () => ({
  bottom: '0',
  left: '0',
  position: 'absolute',
  right: '0',
  top: '0',
  zIndex: '100',
});

const ONBOARDING_TIMEOUT_MS = 3000;

function ChromecastOnboardingOverlay(props) {
  const [showState, setShowState] = useState(false);
  const mounted = useRef(false);
  const timeout = useRef(null);
  const { css } = useFela();

  const hide = () => {
    if (mounted.current) {
      setShowState(false);
      props.onHide();
      storage.setItem('chromecastOnboardingDone', 'true');
    }
  };

  const maybeShow = () => {
    if (showState) {
      return;
    }

    const show = !storage.getItem('chromecastOnboardingDone')
      && props.chromecastState !== CHROMECAST_STATES.UNAVAILABLE
      && props.controlBarHasMouseover;

    setShowState(show);

    if (show) {
      props.onShow();
      window.setTimeout(hide, ONBOARDING_TIMEOUT_MS);
    }
  };

  if (!showState) {
    return null;
  }

  useEffect(() => {
    if (props.controlBarHasMouseover) maybeShow();
  }, [props.controlBarHasMouseover]);

  useEffect(() => {
    mounted.current = true;
    maybeShow();
    return () => {
      mounted.current = false;
      window.clearTimout(timeout.current);
    };
  }, []);

  return (
    <div className={css(wrapperRules)} onClick={hide}>
      <div className={css(bgRules)} />
      <div className={css(labelContainerRules)}>
        <h1 className={css(panelHeadingRules)}>
          <Text id="chromecast.onboardingLabel" />
        </h1>
      </div>
      <Curve />
    </div>
  );
}

ChromecastOnboardingOverlay.propTypes = {
  chromecastState: PropTypes.string.isRequired,
  controlBarHasMouseover: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onShow: PropTypes.func.isRequired,
};

export default React.memo(ChromecastOnboardingOverlay);
